import React, { useContext } from 'react';

import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import type { ExtractInjectionAPI, UiComponentFactoryParams } from '@atlaskit/editor-common/types';
import type { GetMentionNameDetails } from '@atlaskit/editor-mention-types/types';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AIPlugin } from '../../aiPluginType';
import type { AIGlobalOptIn, EditorPluginAIProvider, EndExperience } from '../../types/types';

export interface ContentComponentProps {
	aiOptIn: AIGlobalOptIn;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	appearance: UiComponentFactoryParams['appearance'];
	cancelRovoPrompt?: (() => void) | undefined;
	canInlineStreamChanges: () => boolean;
	editorPluginAIProvider: EditorPluginAIProvider;
	editorView: EditorView;
	endExperience: EndExperience;
	getMentionNameDetails: GetMentionNameDetails;
	/**
	 * Whether the track changes feature is currently displaying changes. Defaults to false.
	 */
	isDisplayingChanges?: boolean;
	isInDocumentStreamingEnabled: boolean;
	isRovoLoading: boolean;
	popupsMountPoint?: UiComponentFactoryParams['popupsMountPoint'];
	providerFactory: ProviderFactory;
	setCancelRovoPrompt?: React.Dispatch<React.SetStateAction<(() => void) | undefined>>;
	setIsRovoLoading: (isLoading: boolean) => void;
	targetId?: string | number;
	wrapperElement: UiComponentFactoryParams['wrapperElement'];
}

const ContentComponentContext = React.createContext<ContentComponentProps | undefined>(undefined);

export const ContentComponentContextProvider: React.Provider<ContentComponentProps | undefined> = ContentComponentContext.Provider;

/**
 * @example
 * ```tsx
 * const {editorView, api} = useContentComponentContext();
 * ```
 */
export const useContentComponentContext = (): ContentComponentProps => {
	const context = useContext(ContentComponentContext);
	if (!context) {
		throw new Error(
			'No Editor view or API available in the context, make sure to have container wrapped with ContentComponentContextProvider',
		);
	}
	return context;
};
